import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { signupSchema, type SignupInput, BRAND } from '@maintenance-tracker/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, ApiError } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { captureNicheFromSearchParams } from '@/lib/niche-signup';
import { BrandLogo } from '@/components/brand/brand-logo';
import { Check } from 'lucide-react';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { TurnstileWidget, HoneypotField } from '@/components/turnstile-widget';
import { usePublicConfig } from '@/lib/public-config';

function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden flex-col justify-between overflow-hidden bg-sidebar p-12 text-white lg:flex">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-brand/20 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-brand/10 blur-3xl" />
        <BrandLogo inverted size="md" to="/" className="relative" />
        <div className="relative max-w-md">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight">
            Never lose a maintenance agreement renewal again.
          </h2>
          <ul className="mt-8 space-y-3.5">
            {[
              'Track every HVAC, plumbing & electrical plan',
              'Auto-remind customers before renewals lapse',
              'Optional renewal page link in reminder emails',
            ].map((f) => (
              <li key={f} className="flex items-center gap-3 text-[15px] text-blue-100">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand/30">
                  <Check className="h-3 w-3 text-white" />
                </span>
                {f}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-[13px] text-sidebar-text">Built for service businesses that rely on recurring customers.</p>
      </div>

      <div className="flex items-center justify-center bg-bg-app p-6">
        <div className="w-full max-w-sm">{children}</div>
      </div>
    </div>
  );
}

function MobileAuthBrand() {
  return (
    <div className="mb-7 lg:hidden">
      <BrandLogo size="md" to="/" />
    </div>
  );
}

export function SignupPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const search = useSearch({ strict: false }) as { niche?: string };
  const { data: publicConfig } = usePublicConfig();
  const turnstileSiteKey = publicConfig?.turnstileSiteKey ?? null;
  const [turnstileToken, setTurnstileToken] = useState('');
  const handleTurnstileToken = useCallback((token: string) => setTurnstileToken(token), []);

  useEffect(() => {
    captureNicheFromSearchParams();
  }, [search.niche]);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignupInput>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      website: '',
    },
  });

  const onSubmit = async (data: SignupInput) => {
    if (turnstileSiteKey && !turnstileToken) {
      setError('email', { message: 'Please complete the security check.' });
      return;
    }
    try {
      await api('/api/v1/auth/signup', {
        method: 'POST',
        body: JSON.stringify({ ...data, turnstile_token: turnstileToken || undefined }),
      });
      await refresh();
      navigate({ to: '/onboarding' });
    } catch (e) {
      const err = e as ApiError;
      setError('email', { message: err.message || 'Sign up failed. Please try again.' });
    }
  };

  return (
    <AuthLayout>
      <MobileAuthBrand />
      <h1 className="text-2xl font-semibold tracking-tight">Create your account</h1>
      <p className="mt-1.5 text-sm text-text-secondary">Start your 7-day Pro trial. No credit card required.</p>
      <form onSubmit={handleSubmit(onSubmit)} className="mt-7 space-y-4">
        <Input label="Your name" error={errors.name?.message} {...register('name')} required />
        <Input label="Email" type="email" error={errors.email?.message} {...register('email')} required />
        <Input label="Password" type="password" error={errors.password?.message} helper="At least 8 characters." {...register('password')} required />
        <Input label="Business name" error={errors.business_name?.message} {...register('business_name')} required />
        <input type="hidden" {...register('timezone')} />
        <HoneypotField {...register('website')} />
        {turnstileSiteKey && (
          <TurnstileWidget siteKey={turnstileSiteKey} onToken={handleTurnstileToken} />
        )}
        <Button
          type="submit"
          loading={isSubmitting}
          disabled={Boolean(turnstileSiteKey && !turnstileToken)}
          className="w-full"
          size="lg"
        >
          Start 7-day free trial
        </Button>
      </form>
      <p className="mt-5 text-center text-sm text-text-secondary">
        Already have an account?{' '}
        <Link to="/login" className="font-medium text-brand hover:underline">Sign in</Link>
      </p>
      <p className="mt-3 text-center text-xs text-text-muted">
        By signing up you agree to our{' '}
        <Link to="/terms" className="underline hover:text-text-secondary">Terms</Link> and{' '}
        <Link to="/privacy" className="underline hover:text-text-secondary">Privacy Policy</Link>.
      </p>
    </AuthLayout>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { data: publicConfig } = usePublicConfig();
  const turnstileSiteKey = publicConfig?.turnstileSiteKey ?? null;
  const [turnstileToken, setTurnstileToken] = useState('');
  const [showTurnstile, setShowTurnstile] = useState(false);
  const handleTurnstileToken = useCallback((token: string) => setTurnstileToken(token), []);
  const { register, handleSubmit, setError, formState: { errors, isSubmitting } } = useForm({
    defaultValues: { email: '', password: '', website: '' },
  });

  const onSubmit = async (data: { email: string; password: string; website?: string }) => {
    if (showTurnstile && turnstileSiteKey && !turnstileToken) {
      setError('email', { message: 'Please complete the security check.' });
      return;
    }
    try {
      await api('/api/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          website: data.website,
          turnstile_token: turnstileToken || undefined,
        }),
      });
      const me = await refresh();
      navigate({ to: me?.settings?.onboardingComplete ? '/dashboard' : '/onboarding' });
    } catch (e) {
      const err = e as ApiError;
      if (err.code === 'BOT_CHECK' || err.message?.includes('security check')) {
        setShowTurnstile(true);
      }
      setError('email', { message: err.message || 'Invalid email or password' });
    }
  };

  return (
    <AuthLayout>
      <MobileAuthBrand />
      <h1 className="text-2xl font-semibold tracking-tight">Welcome back</h1>
      <p className="mt-1.5 text-sm text-text-secondary">Sign in to manage your agreements.</p>
      <form onSubmit={handleSubmit(onSubmit)} className="mt-7 space-y-4">
        <Input label="Email" type="email" error={errors.email?.message} {...register('email')} required />
        <Input label="Password" type="password" {...register('password')} required />
        <HoneypotField {...register('website')} />
        <div className="text-right">
          <Link to="/forgot-password" className="text-[13px] font-medium text-brand hover:underline">
            Forgot password?
          </Link>
        </div>
        {showTurnstile && turnstileSiteKey && (
          <TurnstileWidget siteKey={turnstileSiteKey} onToken={handleTurnstileToken} />
        )}
        <Button
          type="submit"
          loading={isSubmitting}
          disabled={Boolean(showTurnstile && turnstileSiteKey && !turnstileToken)}
          className="w-full"
          size="lg"
        >
          Sign in
        </Button>
      </form>
      <p className="mt-5 text-center text-sm text-text-secondary">
        No account?{' '}
        <Link to="/signup" className="font-medium text-brand hover:underline">Start free trial</Link>
      </p>
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const { data: publicConfig } = usePublicConfig();
  const turnstileSiteKey = publicConfig?.turnstileSiteKey ?? null;
  const [turnstileToken, setTurnstileToken] = useState('');
  const handleTurnstileToken = useCallback((token: string) => setTurnstileToken(token), []);
  const { register, handleSubmit, setError, formState: { errors, isSubmitting, isSubmitSuccessful } } = useForm({
    defaultValues: { email: '', website: '' },
  });

  const onSubmit = async (data: { email: string; website?: string }) => {
    if (turnstileSiteKey && !turnstileToken) {
      setError('email', { message: 'Please complete the security check.' });
      return;
    }
    try {
      await api('/api/v1/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({
          email: data.email,
          website: data.website,
          turnstile_token: turnstileToken || undefined,
        }),
      });
    } catch (e) {
      const err = e as ApiError;
      setError('email', { message: err.message || 'Could not send reset link. Try again later.' });
      throw e;
    }
  };

  return (
    <AuthLayout>
      <MobileAuthBrand />
      <h1 className="text-2xl font-semibold tracking-tight">Reset your password</h1>
      <p className="mt-1.5 text-sm text-text-secondary">
        Enter your email and we'll send you a link to reset it.
      </p>
      {isSubmitSuccessful ? (
        <div className="mt-7 rounded-xl border border-success-border bg-success-subtle p-4 text-sm text-success">
          If an account exists for that email, a reset link is on its way. Check your inbox.
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="mt-7 space-y-4">
          <Input label="Email" type="email" error={errors.email?.message} {...register('email', { required: true })} required />
          <HoneypotField {...register('website')} />
          {turnstileSiteKey && (
            <TurnstileWidget siteKey={turnstileSiteKey} onToken={handleTurnstileToken} />
          )}
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={Boolean(turnstileSiteKey && !turnstileToken)}
            className="w-full"
            size="lg"
          >
            Send reset link
          </Button>
        </form>
      )}
      <p className="mt-5 text-center text-sm text-text-secondary">
        Remembered it?{' '}
        <Link to="/login" className="font-medium text-brand hover:underline">Back to sign in</Link>
      </p>
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const { register, handleSubmit, setError, formState: { errors, isSubmitting } } = useForm({
    defaultValues: { password: '' },
  });

  const onSubmit = async (data: { password: string }) => {
    try {
      await api('/api/v1/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password: data.password }),
      });
      navigate({ to: '/login' });
    } catch (e) {
      setError('password', { message: (e as ApiError).message || 'That reset link is invalid or expired.' });
    }
  };

  return (
    <AuthLayout>
      <MobileAuthBrand />
      <h1 className="text-2xl font-semibold tracking-tight">Choose a new password</h1>
      <p className="mt-1.5 text-sm text-text-secondary">Enter a new password for your account.</p>
      {!token ? (
        <div className="mt-7 rounded-xl border border-danger-border bg-danger-subtle p-4 text-sm text-danger">
          This reset link is missing its token. Request a new link from the{' '}
          <Link to="/forgot-password" className="font-medium underline">forgot password</Link> page.
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="mt-7 space-y-4">
          <Input
            label="New password"
            type="password"
            helper="At least 8 characters."
            error={errors.password?.message}
            {...register('password', { required: true, minLength: 8 })}
            required
          />
          <Button type="submit" loading={isSubmitting} className="w-full" size="lg">Update password</Button>
        </form>
      )}
      <p className="mt-5 text-center text-sm text-text-secondary">
        <Link to="/login" className="font-medium text-brand hover:underline">Back to sign in</Link>
      </p>
    </AuthLayout>
  );
}

export function VerifyEmailPage() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-app p-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-bg-surface p-8 text-center shadow-card">
        <div className="mx-auto mb-4 flex justify-center">
          <BrandLogo size="lg" to="/" showName={false} />
        </div>
        <h1 className="text-xl font-semibold">{BRAND.name}</h1>
        <h2 className="mt-2 text-lg font-semibold">Verify your email</h2>
        {token ? (
          <VerifyButton token={token} />
        ) : (
          <p className="mt-2 text-sm text-text-secondary">Check your inbox for a verification link to continue.</p>
        )}
      </div>
    </div>
  );
}

function VerifyButton({ token }: { token: string }) {
  return (
    <Button
      className="mt-5"
      onClick={async () => {
        await api('/api/v1/auth/verify-email', { method: 'POST', body: JSON.stringify({ token }) });
        window.location.href = '/onboarding';
      }}
    >
      Verify email
    </Button>
  );
}
